import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import React, { FC, useState } from "react";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import useQuoteCartProduct from "@WhiteCap/Hooks/useQuoteCartProduct";
import quoteCountHandler from "@WhiteCap/Handlers/QuoteCart/QuoteCartHandler";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import BrickCard from "@WhiteCap/Widgets/Basic/BrickCard";
import getColor from "@insite/mobius/utilities/getColor";

interface OwnProps {
    extendedStyles?: BrickCartCardStyles;
    css?: StyledProp;
}

type Props = OwnProps & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

export interface BrickCartCardStyles {
    cardWrapper?: InjectableCss;
    cardInfoWrapper?: InjectableCss;
    productName?: InjectableCss;
    productAttribute?: InjectableCss;
    imgWrapper?: InjectableCss;
    deleteBtnLink?: InjectableCss;
    deleteWrapper?: InjectableCss;
}

export const brickCartCardStyles: BrickCartCardStyles = {
    cardWrapper: {
        css: css`
            display: flex;
            background: ${getColor("primary.contrast")};
            box-shadow: 4px 4px 8px 2px rgba(0, 0, 0, 0.16);
            padding: 16px;
            position: relative;
            margin-bottom: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: block;
                            margin-bottom: 20px;
                            &:last-child {
                                margin-bottom: 0px;
                            }
                        `,
                        css`
                            display: block;
                            margin-bottom: 20px;
                            &:last-child {
                                margin-bottom: 0px;
                            }
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    productName: {
        css: css`
            font-size: 18px;
            line-height: 26px;
            margin-bottom: 16px;
            font-style: normal;
            font-weight: 500;
            strong {
                font-weight: 400;
            }
        `,
    },
    productAttribute: {
        css: css`
            div {
                margin-bottom: 10px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin-bottom: 0;
                            `,
                            css`
                                margin-bottom: 0;
                            `,
                            null,
                        ],
                        "max",
                    )}
                label {
                    font-weight: 700;
                    font-size: 16px;
                    line-height: 26px;
                }
                span {
                    font-weight: 400;
                    font-size: 16px;
                    line-height: 26px;
                    margin-left: 3px;
                }
            }
        `,
    },
    imgWrapper: {
        css: css`
            margin-right: 16px;
        `,
    },
    deleteBtnLink: {
        css: css`
            width: 20px;
            height: 20px;
        `,
    },
    deleteWrapper: {
        css: css`
            position: absolute;
            bottom: 10px;
            right: 16px;
        `,
    },
};

const mapDispatchToProps = {
    quoteCountHandler,
};

const brickCard: FC<Props> = ({ quoteCountHandler, extendedStyles }) => {
    const [styles] = useState(() => mergeToNew(brickCartCardStyles, extendedStyles));
    const { cart, deleteFromLocalStorage } = useQuoteCartProduct();
    const [products, setProducts] = useState(cart.products);

    const handleDeleteBrickCard = (productId: string) => {
        deleteFromLocalStorage(productId);
        setProducts(products.filter(product => product.id !== productId));
    };

    React.useEffect(() => {
        if (cart) {
            quoteCountHandler({ quoteCount: cart.quoteCount });
        }
    }, [cart]);

    return (
        <>
            {cart?.products.map(product => (
                <BrickCard key={product.id} product={product} handleDeleteBrickCard={handleDeleteBrickCard} />
            ))}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(withHistory(brickCard)),
    definition: {
        displayName: "Brick Qoute Card",
        group: "Common",
    },
};

export default widgetModule;
